// @ts-check
// Shared activities domain client.
//
// Scope: the core `/api/activities` endpoints that are identical across web,
// mobile, and Guardian (list, detail, user list, create, update, delete,
// confirm, cancel). Path builders are exported for app-specific endpoints.
//
// Legacy category and activity participation path builders remain exported for
// compatibility. They are not mounted by the main backend. Live category reads
// filter the maintained list; participation uses the sessions client.

import { joinPath, withQuery } from '../path-utils.js';
import { readActivityIdentifier, readActivityDetailQuery, readActivityWriteRequest } from '../activity-requests.js';
import { readUserIdentifier } from '../user-requests.js';
export { nextActivitySession } from '../activity-session-selection.js';
export {readOccurrenceReadContext, occurrenceCalendarRequestScope, assertOccurrenceResponseScope} from '../occurrence-read-contract.js';
export { readActivityListResponse, readActivityRecordResponse, readActivityCreationResponse, readActivityConfirmationResponse, readActivityDeletionResponse, readActivityVenuesResponse, ActivityResponseContractError } from '../activity-responses.js';
export { ActivityRequestContractError, readActivityIdentifier, readActivityDetailQuery, readActivityVenueQuery, readActivityWriteRequest } from '../activity-requests.js';

/**
 * @typedef {object} ActivitiesAdapter
 * @property {(path: string, options?: unknown) => Promise<unknown>} get
 * @property {(path: string, body?: unknown, options?: unknown) => Promise<unknown>} post
 * @property {(path: string, body?: unknown, options?: unknown) => Promise<unknown>} patch
 * @property {(path: string, options?: unknown) => Promise<unknown>} delete
 */

export const ACTIVITY_PATHS = {
  collection: '/api/activities',
  create: '/api/activities/create',
};

export const buildActivityPath = (id) =>
  joinPath(ACTIVITY_PATHS.collection, id);

export const buildActivityUserPath = (userId) =>
  joinPath(ACTIVITY_PATHS.collection, 'user', userId);

export const buildActivityActionPath = (id, action) =>
  `${buildActivityPath(id)}/${action}`;

export const buildActivityMemberActionPath = (id, action, memberId) =>
  joinPath(buildActivityActionPath(id, action), memberId);

export const buildActivityCategoryPath = (category) =>
  `/api/activities/${category}`;

export const buildActivityEligibleVenuesPath = (activityId) =>
  `${buildActivityPath(activityId)}/eligible-venues`;

export const buildActivityVenuesPath = (activityId) =>
  `${buildActivityPath(activityId)}/venues`;

export const buildActivityConfirmPath = (activityId, { retry } = {}) => {
  const path = buildActivityActionPath(activityId, 'confirm');
  return withQuery(path, { retry });
};

/**
 * @param {ActivitiesAdapter} adapter
 */
export const createActivitiesClient = (adapter) => {
  if (!adapter || typeof adapter !== 'object') {
    throw new TypeError('createActivitiesClient requires a transport adapter.');
  }

  return {
    list(options = {}) {
      return adapter.get(ACTIVITY_PATHS.collection, options);
    },

    getById(activityId, options = {}) {
      readActivityIdentifier(activityId);
      readActivityDetailQuery(options.params);
      return adapter.get(buildActivityPath(activityId), options);
    },

    listForUser(userId, options = {}) {
      readUserIdentifier(userId);
      return adapter.get(buildActivityUserPath(userId), options);
    },

    create(payload, options = {}) {
      return adapter.post(ACTIVITY_PATHS.create, readActivityWriteRequest(payload, 'create'), options);
    },

    update(activityId, updates, options = {}) {
      readActivityIdentifier(activityId);
      return adapter.patch(buildActivityPath(activityId), readActivityWriteRequest(updates), options);
    },

    remove(activityId, options = {}) {
      readActivityIdentifier(activityId);
      return adapter.delete(buildActivityPath(activityId), options);
    },

    confirm(activityId, options = {}) {
      readActivityIdentifier(activityId);
      return adapter.post(buildActivityActionPath(activityId, 'confirm'), null, options);
    },

    cancel(activityId, options = {}) {
      readActivityIdentifier(activityId);
      return adapter.patch(buildActivityActionPath(activityId, 'cancel'), null, options);
    },
  };
};
